// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { notesAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatDate } from "@/lib/utils";
import jsPDF from "jspdf";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useEditor, EditorContent } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { Underline } from "@tiptap/extension-underline";
import { Highlight } from "@tiptap/extension-highlight";
import { TaskList } from "@tiptap/extension-task-list";
import { TaskItem } from "@tiptap/extension-task-item";
import { Link as TiptapLink } from "@tiptap/extension-link";
import { Image as TiptapImage } from "@tiptap/extension-image";
import { Placeholder } from "@tiptap/extension-placeholder";
import { TextAlign } from "@tiptap/extension-text-align";
import { Table as TiptapTable } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { HorizontalRule } from "@tiptap/extension-horizontal-rule";
import { CharacterCount } from "@tiptap/extension-character-count";
import { Color } from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import { SlashMenuExtension, slashSuggestionConfig } from "./slashMenu";
import {
  Plus, Search, StickyNote, FolderPlus, Folder, FolderOpen, ChevronRight,
  Pin, PinOff, Trash2, Share2, MoreHorizontal, Bold, Italic, Underline as UnderlineIcon,
  Strikethrough, Code, Heading1, Heading2, Heading3, List, ListOrdered,
  CheckSquare, Quote, Minus, ImageIcon, Link as LinkIcon, AlignLeft,
  AlignCenter, AlignRight, Table as TableIcon, Highlighter, Redo, Undo,
  PanelLeftClose, PanelLeft, Maximize2, Minimize2, Clock, Save, Loader2,
  FileText, Hash, Users, Eye, Edit3, X, Check, Download, Palette
} from "lucide-react";
import "./NotesPage.css";

// ──── Toolbar Component ────
const EditorToolbar = ({ editor }) => {
  const [showColorPicker, setShowColorPicker] = useState(false);

  if (!editor) return null;

  const addImage = () => {
    const url = window.prompt("URL de l'image :");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  const addLink = () => {
    const url = window.prompt("URL du lien :");
    if (url) editor.chain().focus().setLink({ href: url }).run();
    else editor.chain().focus().unsetLink().run();
  };

  const Btn = ({ action, active, icon: Icon, title }) => (
    <button onPointerDown={(e) => e.preventDefault()} onClick={action} className={active ? "active" : ""} title={title}>
      <Icon size={16} />
    </button>
  );

  const Div = () => <span className="toolbar-divider" />;

  return (
    <div className="notes-toolbar">
      <div className="toolbar-group">
        <Btn action={() => editor.chain().focus().undo().run()} icon={Undo} title="Annuler" />
        <Btn action={() => editor.chain().focus().redo().run()} icon={Redo} title="Refaire" />
      </div>
      <Div />
      <div className="toolbar-group">
        <Btn action={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} icon={Heading1} title="Titre 1" />
        <Btn action={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} icon={Heading2} title="Titre 2" />
        <Btn action={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} icon={Heading3} title="Titre 3" />
      </div>
      <Div />
      <div className="toolbar-group">
        <Btn action={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} icon={Bold} title="Gras" />
        <Btn action={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} icon={Italic} title="Italique" />
        <Btn action={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} icon={UnderlineIcon} title="Souligné" />
        <Btn action={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} icon={Strikethrough} title="Barré" />
        <Btn action={() => editor.chain().focus().toggleHighlight().run()} active={editor.isActive("highlight")} icon={Highlighter} title="Surligner" />
        <Btn action={() => editor.chain().focus().toggleCode().run()} active={editor.isActive("code")} icon={Code} title="Code" />
      </div>
      <Div />
      <div className="toolbar-group">
        <Btn action={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} icon={List} title="Liste" />
        <Btn action={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} icon={ListOrdered} title="Liste numérotée" />
        <Btn action={() => editor.chain().focus().toggleTaskList().run()} active={editor.isActive("taskList")} icon={CheckSquare} title="Tâches" />
      </div>
      <Div />
      <div className="toolbar-group">
        <Btn action={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} icon={Quote} title="Citation" />
        <Btn action={() => editor.chain().focus().setHorizontalRule().run()} icon={Minus} title="Séparateur" />
        <Btn action={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive("codeBlock")} icon={Code} title="Bloc de code" />
      </div>
      <Div />
      <div className="toolbar-group">
        <Btn action={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} icon={AlignLeft} title="Gauche" />
        <Btn action={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} icon={AlignCenter} title="Centre" />
        <Btn action={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} icon={AlignRight} title="Droite" />
      </div>
      <Div />
      <div className="toolbar-group">
        <Btn action={addLink} active={editor.isActive("link")} icon={LinkIcon} title="Lien" />
        <Btn action={addImage} icon={ImageIcon} title="Image" />
        <Btn action={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} icon={TableIcon} title="Tableau" />
      </div>
      <Div />
      <div className="toolbar-group" style={{ position: "relative" }}>
        <button 
          className={showColorPicker ? "active" : ""} 
          title="Couleur du texte" 
          style={{ display: "flex", alignItems: "center" }}
          onClick={() => setShowColorPicker(!showColorPicker)}
          onPointerDown={(e) => e.preventDefault()}
        >
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              border: "1px solid hsl(var(--border))",
              backgroundColor: editor.getAttributes("textStyle").color || "#000000"
            }}
          />
        </button>

        {showColorPicker && (
          <div 
            onMouseDown={(e) => e.preventDefault()}
            style={{ 
              position: "absolute", top: "100%", right: 0, zIndex: 50, marginTop: 4,
              width: 140, padding: 8, background: "hsl(var(--background))", 
              border: "1px solid hsl(var(--border))", borderRadius: 6,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: "hsl(var(--muted-foreground))" }}>
              Couleurs standards
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {[
                "#000000", "#434343", "#666666", "#999999", 
                "#e60000", "#ff9900", "#ffff00", "#008a00", 
                "#0066cc", "#9933ff", "#e60066", "#ffffff"
              ].map((color) => (
                <button
                  key={color}
                  onPointerDown={(e) => e.preventDefault()}
                  onClick={() => {
                    editor.chain().focus().setColor(color).run();
                    setShowColorPicker(false);
                  }}
                  style={{
                    width: 20,
                    height: 20,
                    backgroundColor: color,
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 4,
                    cursor: "pointer",
                    padding: 0
                  }}
                  title={color}
                />
              ))}
            </div>
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid hsl(var(--border))" }}>
              <button 
                onPointerDown={(e) => e.preventDefault()}
                onClick={() => {
                  editor.chain().focus().unsetColor().run();
                  setShowColorPicker(false);
                }}
                style={{ width: "100%", textAlign: "left", fontSize: 12, padding: "4px 8px", background: "none", border: "none", cursor: "pointer", borderRadius: 4 }}
                className="hover:bg-muted"
              >
                Couleur par défaut
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ──── Main Component ────
export default function NotesPage() {
  const { workspaceId } = useParams();
  const { user } = useAuth();
  const userId = user?.id;

  // State
  const [notes, setNotes] = useState([]);
  const [folders, setFolders] = useState([]);
  const [tags, setTags] = useState([]);
  const [activeNote, setActiveNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState("saved"); // saved | saving | unsaved
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFolder, setActiveFolder] = useState(null);
  const [activeTag, setActiveTag] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [titleValue, setTitleValue] = useState("");
  const [editingFolder, setEditingFolder] = useState(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState({});

  const saveTimerRef = useRef(null);
  const activeNoteRef = useRef(null);
  activeNoteRef.current = activeNote;

  // ── TipTap Editor ──
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Highlight.configure({ multicolor: true }),
      TaskList,
      TaskItem.configure({ nested: true }),
      TiptapLink.configure({ openOnClick: false }),
      TiptapImage.configure({ allowBase64: true }),
      Placeholder.configure({ placeholder: "Commencez à écrire... Utilisez '/' pour les commandes" }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TiptapTable.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      HorizontalRule,
      CharacterCount,
      TextStyle,
      Color.configure({ types: ["textStyle"] }),
      SlashMenuExtension.configure({
        suggestion: slashSuggestionConfig,
      }),
    ],
    onUpdate: ({ editor }) => {
      setSaveStatus("unsaved");
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        autoSave(editor.getJSON());
      }, 1500);
    },
  });

  // ── Load Data ──
  useEffect(() => {
    if (workspaceId && userId) loadAll();
  }, [workspaceId, userId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [notesRes, foldersRes, tagsRes] = await Promise.all([
        notesAPI.list(workspaceId, userId),
        notesAPI.listFolders(workspaceId, userId),
        notesAPI.listTags(workspaceId, userId),
      ]);
      setNotes(notesRes.data);
      setFolders(foldersRes.data);
      setTags(tagsRes.data || []);
    } catch (err) {
      console.error("Failed to load notes:", err);
      toast.error("Erreur lors du chargement des notes");
    } finally {
      setLoading(false);
    }
  };

  // ── Auto-save ──
  const autoSave = useCallback(async (content) => {
    const note = activeNoteRef.current;
    if (!note || !userId) return;
    setSaving(true);
    setSaveStatus("saving");
    try {
      await notesAPI.update(note.id, userId, { title: note.title, content });
      setSaveStatus("saved");
    } catch (err) {
      console.error("Auto-save failed:", err);
      setSaveStatus("unsaved");
    } finally {
      setSaving(false);
    }
  }, [userId]);

  // ── Select Note ──
  const selectNote = async (note) => {
    // Save current note before switching
    if (activeNote && editor && saveStatus === "unsaved") {
      await autoSave(editor.getJSON());
    }
    try {
      const res = await notesAPI.get(note.id, userId);
      const full = res.data;
      setActiveNote(full);
      setTitleValue(full.title || "");
      if (editor) {
        editor.commands.setContent(full.content || { type: "doc", content: [{ type: "paragraph" }] });
      }
      setSaveStatus("saved");
    } catch (err) {
      toast.error("Erreur lors du chargement de la note");
    }
  };

  // ── CRUD ──
  const createNote = async () => {
    try {
      const res = await notesAPI.create(workspaceId, userId, {
        title: "Note sans titre",
        content: { type: "doc", content: [{ type: "paragraph" }] },
        folder_id: activeFolder,
      });
      const newNote = res.data;
      setNotes((prev) => [newNote, ...prev]);
      selectNote(newNote);
      toast.success("Note créée");
    } catch (err) {
      toast.error("Erreur lors de la création");
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Supprimer cette note ?")) return;
    try {
      await notesAPI.delete(noteId, userId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      if (activeNote?.id === noteId) {
        setActiveNote(null);
        editor?.commands.clearContent();
      }
      toast.success("Note supprimée");
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const togglePin = async (noteId) => {
    try {
      await notesAPI.togglePin(noteId, userId);
      setNotes((prev) => prev.map((n) => n.id === noteId ? { ...n, is_pinned: !n.is_pinned } : n));
      if (activeNote?.id === noteId) setActiveNote((p) => ({ ...p, is_pinned: !p.is_pinned }));
    } catch (err) {
      toast.error("Erreur");
    }
  };

  // ── Title Update ──
  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setTitleValue(newTitle);
    setActiveNote((prev) => prev ? { ...prev, title: newTitle } : prev);
    setNotes((prev) => prev.map((n) => n.id === activeNote?.id ? { ...n, title: newTitle } : n));
    setSaveStatus("unsaved");
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      if (editor) autoSave(editor.getJSON());
    }, 1500);
  };

  // ── Export Functions ──
  const exportHTML = () => {
    if (!editor || !activeNote) return;
    const html = editor.getHTML();
    const fullHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${activeNote.title || 'Note'}</title><style>body{font-family:sans-serif;padding:2rem;max-width:800px;margin:0 auto;line-height:1.6}img{max-width:100%}table{border-collapse:collapse;width:100%}th,td{border:1px solid #ccc;padding:8px}</style></head><body><h1>${activeNote.title || 'Note'}</h1>${html}</body></html>`;
    const blob = new Blob([fullHtml], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${activeNote.title || 'note'}.html`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Note exportée en HTML");
  };

  const exportPDF = () => {
    if (!editor || !activeNote) return;
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4"
    });
    
    // Create a temporary hidden div with the content to render
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = `<h2>${activeNote.title || 'Note'}</h2>${editor.getHTML()}`;
    tempDiv.style.width = "500px";
    tempDiv.style.padding = "20px";
    tempDiv.style.fontFamily = "sans-serif";
    tempDiv.style.fontSize = "12px";
    
    // Hide images that are base64 to avoid rendering issues with simple html method
    // or we can just use doc.html
    document.body.appendChild(tempDiv);
    
    doc.html(tempDiv, {
      callback: function (doc) {
        doc.save(`${activeNote.title || 'note'}.pdf`);
        document.body.removeChild(tempDiv);
        toast.success("Note exportée en PDF");
      },
      x: 30,
      y: 30,
      width: 500,
      windowWidth: 500
    });
  };

  // ── Folders ──
  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const res = await notesAPI.createFolder(workspaceId, userId, { name: newFolderName.trim() });
      setFolders((prev) => [...prev, res.data]);
      setNewFolderName("");
      setShowNewFolder(false);
      toast.success("Dossier créé");
    } catch (err) {
      toast.error("Erreur lors de la création du dossier");
    }
  };

  const deleteFolder = async (folderId) => {
    if (!window.confirm("Supprimer ce dossier ? Les notes seront déplacées à la racine.")) return;
    try {
      await notesAPI.deleteFolder(folderId, userId);
      setFolders((prev) => prev.filter((f) => f.id !== folderId));
      if (activeFolder === folderId) setActiveFolder(null);
      loadAll();
      toast.success("Dossier supprimé");
    } catch (err) {
      toast.error("Erreur");
    }
  };

  // ── Filtering ──
  const filteredNotes = notes
    .filter((n) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return n.title?.toLowerCase().includes(q) || n.tags?.some((t) => t.toLowerCase().includes(q));
      }
      if (activeTag) return n.tags?.includes(activeTag);
      if (activeFolder) return n.folder_id === activeFolder;
      if (activeFolder === null && !activeTag) return true;
      return true;
    })
    .sort((a, b) => {
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;
      return new Date(b.updated_at) - new Date(a.updated_at);
    });

  // ── Extract preview text ──
  const getPreview = (note) => {
    if (!note.content) return "";
    const extractText = (node) => {
      if (node.text) return node.text;
      if (node.content) return node.content.map(extractText).join(" ");
      return "";
    };
    return extractText(note.content).slice(0, 80);
  };

  // ── Loading State ──
  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="notes-app">
          <div className="notes-empty-state">
            <Loader2 className="empty-icon animate-spin" />
            <h3>Chargement des notes...</h3>
          </div>
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <div className={`notes-app ${focusMode ? "focus-mode" : ""}`}>
        {/* ── Sidebar ── */}
        <aside className={`notes-sidebar ${sidebarCollapsed ? "collapsed" : ""}`}>
          <div className="notes-sidebar-header">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
                <StickyNote size={18} /> Notes
              </h2>
              <Button variant="ghost" size="icon" style={{ height: 28, width: 28 }} onClick={createNote} title="Nouvelle note">
                <Plus size={16} />
              </Button>
            </div>
            <div className="notes-sidebar-search">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="notes-sidebar-content">
            {/* All Notes */}
            <div
              className={`folder-item ${activeFolder === null && !activeTag ? "active" : ""}`}
              onClick={() => { setActiveFolder(null); setActiveTag(null); }}
            >
              <FileText className="folder-icon" />
              <span>Toutes les notes</span>
              <span className="folder-count">{notes.length}</span>
            </div>

            {/* Folders */}
            {folders.map((folder) => {
              const count = notes.filter((n) => n.folder_id === folder.id).length;
              const isExpanded = expandedFolders[folder.id];
              return (
                <div key={folder.id}>
                  <div
                    className={`folder-item ${activeFolder === folder.id ? "active" : ""}`}
                    onClick={() => { setActiveFolder(folder.id); setActiveTag(null); setExpandedFolders((p) => ({ ...p, [folder.id]: !p[folder.id] })); }}
                  >
                    {isExpanded ? <FolderOpen className="folder-icon" /> : <Folder className="folder-icon" />}
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{folder.name}</span>
                    <span className="folder-count">{count}</span>
                    <button
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex" }}
                      onClick={(e) => { e.stopPropagation(); deleteFolder(folder.id); }}
                      title="Supprimer"
                    >
                      <X size={12} style={{ color: "hsl(var(--muted-foreground))" }} />
                    </button>
                  </div>
                </div>
              );
            })}

            {/* New Folder */}
            {showNewFolder ? (
              <div style={{ display: "flex", gap: 4, padding: "4px 10px", alignItems: "center" }}>
                <input
                  autoFocus
                  style={{ flex: 1, fontSize: 13, padding: "4px 8px", borderRadius: 6, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))", outline: "none" }}
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") createFolder(); if (e.key === "Escape") setShowNewFolder(false); }}
                  placeholder="Nom du dossier"
                />
                <button style={{ background: "none", border: "none", cursor: "pointer", display: "flex" }} onClick={createFolder}><Check size={14} /></button>
                <button style={{ background: "none", border: "none", cursor: "pointer", display: "flex" }} onClick={() => setShowNewFolder(false)}><X size={14} /></button>
              </div>
            ) : (
              <div className="folder-item" onClick={() => setShowNewFolder(true)} style={{ color: "hsl(var(--primary))" }}>
                <FolderPlus className="folder-icon" />
                <span>Nouveau dossier</span>
              </div>
            )}

            {/* Tags */}
            {tags.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: "hsl(var(--muted-foreground))", padding: "12px 10px 4px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Tags</div>
                {tags.map((tag) => (
                  <div
                    key={tag}
                    className={`folder-item ${activeTag === tag ? "active" : ""}`}
                    onClick={() => { setActiveTag(tag); setActiveFolder(null); }}
                  >
                    <Hash size={14} className="folder-icon" />
                    <span>{tag}</span>
                  </div>
                ))}
              </>
            )}

            {/* Separator */}
            <div style={{ borderTop: "1px solid hsl(var(--border) / 0.5)", margin: "12px 0" }} />

            {/* Note List */}
            {filteredNotes.map((note) => (
              <div
                key={note.id}
                className={`note-list-item ${activeNote?.id === note.id ? "active" : ""}`}
                onClick={() => selectNote(note)}
              >
                <div className="note-title">
                  {note.is_pinned && <Pin size={12} className="pin-badge" />}
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {note.title || "Sans titre"}
                  </span>
                  {note.shared_with?.length > 0 && <span className="shared-badge">Partagé</span>}
                </div>
                <div className="note-preview">{getPreview(note)}</div>
                <div className="note-meta">
                  <Clock size={10} />
                  <span>{formatDate(note.updated_at)}</span>
                  {note.tags?.map((t) => <span key={t} className="note-tag">{t}</span>)}
                </div>
              </div>
            ))}

            {filteredNotes.length === 0 && (
              <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: "hsl(var(--muted-foreground))" }}>
                {searchQuery ? "Aucun résultat" : "Aucune note dans ce dossier"}
              </div>
            )}
          </div>
        </aside>

        {/* ── Editor Area ── */}
        <div className="notes-editor-area">
          {activeNote ? (
            <>
              {/* Header */}
              <div className="notes-editor-header">
                <Button variant="ghost" size="icon" style={{ height: 32, width: 32 }} onClick={() => setSidebarCollapsed(!sidebarCollapsed)} title={sidebarCollapsed ? "Afficher la barre latérale" : "Masquer la barre latérale"}>
                  {sidebarCollapsed ? <PanelLeft size={16} /> : <PanelLeftClose size={16} />}
                </Button>
                <div className="flex-1 px-4 text-sm font-medium text-muted-foreground truncate">
                  {titleValue || "Nouvelle note"}
                </div>
                <Button variant="ghost" size="icon" style={{ height: 32, width: 32 }} onClick={() => togglePin(activeNote.id)} title={activeNote.is_pinned ? "Détacher" : "Épingler"}>
                  {activeNote.is_pinned ? <PinOff size={16} /> : <Pin size={16} />}
                </Button>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" style={{ height: 32, width: 32 }} title="Exporter">
                      <Download size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={exportHTML}>Exporter en HTML</DropdownMenuItem>
                    <DropdownMenuItem onClick={exportPDF}>Exporter en PDF</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button variant="ghost" size="icon" style={{ height: 32, width: 32 }} onClick={() => setFocusMode(!focusMode)} title={focusMode ? "Quitter le mode focus" : "Mode focus"}>
                  {focusMode ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </Button>
                <Button variant="ghost" size="icon" style={{ height: 32, width: 32 }} className="text-destructive" onClick={() => deleteNote(activeNote.id)} title="Supprimer">
                  <Trash2 size={16} />
                </Button>
              </div>

              {/* Toolbar */}
              <EditorToolbar editor={editor} />

              {/* Editor */}
              <div className="notes-editor-content">
                <input
                  className="notion-like-title-input"
                  value={titleValue}
                  onChange={handleTitleChange}
                  placeholder="Titre de la note"
                />
                <EditorContent editor={editor} />
              </div>

              {/* Status Bar */}
              <div className="notes-status-bar">
                <div className="status-left">
                  <span className={`save-indicator ${saveStatus}`}>
                    {saveStatus === "saving" && <><Loader2 size={12} className="animate-spin" /> Sauvegarde...</>}
                    {saveStatus === "saved" && <><Check size={12} /> Sauvegardé</>}
                    {saveStatus === "unsaved" && <><Edit3 size={12} /> Non sauvegardé</>}
                  </span>
                </div>
                <div className="status-right">
                  {editor && (
                    <>
                      <span>{editor.storage.characterCount.characters()} caractères</span>
                      <span>{editor.storage.characterCount.words()} mots</span>
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="notes-empty-state">
              <StickyNote className="empty-icon" />
              <h3>Sélectionnez ou créez une note</h3>
              <p>Choisissez une note dans la barre latérale ou créez-en une nouvelle pour commencer.</p>
              <Button onClick={createNote} className="gap-2" style={{ marginTop: 8 }}>
                <Plus size={16} /> Nouvelle note
              </Button>
            </div>
          )}
        </div>
      </div>
    </WorkspaceLayout>
  );
}
